/**
 * Device detection utilities for optimal OAuth flow selection
 */

/**
 * Detect if the current device is mobile
 * Mobile devices often block popups or handle them poorly
 */
export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  ) || window.innerWidth < 768;
};

/**
 * Determine if the current environment supports popup-based OAuth
 * Currently disabled - redirect flow is more reliable across browsers
 * (Safari has localStorage isolation between popup and parent windows)
 */
export const supportsPopup = (): boolean => {
  // Force redirect flow for all browsers to avoid cross-window session issues
  return false;
};

/**
 * Detects if the current device is a touch-enabled device (mobile/tablet)
 * Uses multiple detection methods for reliability
 */
export function isTouchDevice(): boolean {
  // Check for touch events support
  if (typeof window === 'undefined') {
    return false;
  }

  // Primary detection: maxTouchPoints
  if (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) {
    return true;
  }

  // Fallback: ontouchstart event
  if ('ontouchstart' in window) {
    return true;
  }

  // Additional check for older browsers
  if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
    return true;
  }

  return false;
}

/**
 * Convert pixel coordinates to percentage-based coordinates
 * @param pixelX - X coordinate in pixels
 * @param pixelY - Y coordinate in pixels
 * @param pageWidth - Page width in pixels
 * @param pageHeight - Page height in pixels
 * @returns Object with percentage-based x and y coordinates
 */
export function pixelsToPercentage(
  pixelX: number,
  pixelY: number,
  pageWidth: number,
  pageHeight: number
): { x: number; y: number } {
  return {
    x: (pixelX / pageWidth) * 100,
    y: (pixelY / pageHeight) * 100,
  };
}

/**
 * Convert percentage-based coordinates to pixel coordinates
 * @param percentX - X coordinate as percentage (0-100)
 * @param percentY - Y coordinate as percentage (0-100)
 * @param pageWidth - Page width in pixels
 * @param pageHeight - Page height in pixels
 * @returns Object with pixel-based x and y coordinates
 */
export function percentageToPixels(
  percentX: number,
  percentY: number,
  pageWidth: number,
  pageHeight: number
): { x: number; y: number } {
  return {
    x: (percentX / 100) * pageWidth,
    y: (percentY / 100) * pageHeight,
  };
}
